import { useEffect, useRef, useState } from 'react';

import { volumeToLevel } from '~/modules/elements/volume-level';
import { playerTextColor } from '~/modules/game-engine/drawing/styles';
import InputManager from '~/modules/game-engine/input/input-manager';
import { inputStatus } from '~/modules/game-engine/input/interface';
import { usePlayerMicData } from '~/modules/hooks/players/use-player-mic';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { cn } from '~/utils/cn';

const STATUS_POLL_MS = 333;

/** Every singer's mic status from one timer, re-rendering only when one of them changes. */
function useMicStatuses(players: PlayerNumber[]) {
  const key = players.join(',');
  const [statuses, setStatuses] = useState<inputStatus[]>(() => players.map(() => 'ok'));

  useEffect(() => {
    const look = () => {
      const next = players.map((number) => InputManager.getInputStatus(number));
      setStatuses((current) => (current.join() === next.join() ? current : next));
    };

    look();
    const timer = setInterval(look, STATUS_POLL_MS);

    return () => clearInterval(timer);
    // `key` stands for the list, which the caller rebuilds every render.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return statuses;
}

/**
 * Every singer's mic as one slim level bar, side by side, for when the band is shared by three or
 * more. A mic with a problem is ringed and named in an alert above the band, so the strip stays small.
 */
export default function MicLevelStrip({ players }: { players: PlayerNumber[] }) {
  const statuses = useMicStatuses(players);

  return (
    <>
      <div className="absolute right-3 bottom-full mb-2 flex flex-col items-end gap-1.5" data-test="mic-alerts">
        {players.map((number, index) => (
          <MicAlert key={number} playerNumber={number} status={statuses[index] ?? 'ok'} />
        ))}
      </div>
      <div className="absolute inset-y-0 right-3 flex items-center gap-1.5" data-test="mic-level-strip">
        {players.map((number, index) => (
          <LevelBar key={number} playerNumber={number} status={statuses[index] ?? 'ok'} />
        ))}
      </div>
    </>
  );
}

function LevelBar({ playerNumber, status }: { playerNumber: PlayerNumber; status: inputStatus }) {
  const fill = useRef<HTMLDivElement | null>(null);

  // Written straight to the DOM: the level lands many times a second and needs no render.
  usePlayerMicData(playerNumber, ([volume]) => {
    if (fill.current) fill.current.style.transform = `scaleY(${volumeToLevel(volume)})`;
  });

  return (
    <div className="flex flex-col items-center gap-1" data-test="player-mic-status" data-status={status}>
      <div
        className={cn(
          'relative h-16 w-3 overflow-hidden rounded-[0.1875rem] bg-white/10',
          // Outside the bar, so the level filling it never hides the warning.
          status !== 'ok' && 'outline-2 outline-offset-1',
          status === 'unavailable' && 'outline-[#ff4d4d]',
          (status === 'unstable' || status === 'silent') && 'outline-[#ffb020]',
        )}>
        {status !== 'unavailable' && (
          <div
            ref={fill}
            className="absolute inset-0 origin-bottom"
            style={{ background: playerTextColor(playerNumber), transform: 'scaleY(0)' }}
            data-test="mic-volume-indicator"
          />
        )}
      </div>
      <span
        className={cn(
          'text-xs leading-none font-black',
          status === 'ok' && 'text-white/75',
          (status === 'unstable' || status === 'silent') && 'text-amber-300',
          status === 'unavailable' && 'text-red-400',
        )}>
        {status === 'ok' ? playerNumber + 1 : status === 'unavailable' ? '×' : '!'}
      </span>
    </div>
  );
}

function MicAlert({ playerNumber, status }: { playerNumber: PlayerNumber; status: inputStatus }) {
  if (status === 'ok') return null;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold whitespace-nowrap',
        status === 'unavailable' ? 'bg-red-950/95 text-red-100' : 'bg-amber-950/95 text-amber-100',
      )}
      data-test={`status-${status}`}>
      <span className="size-2.5 shrink-0 rounded-full" style={{ background: playerTextColor(playerNumber) }} />
      <span>
        <span className="ph-no-capture">{PlayersManager.getPlayer(playerNumber)?.getName()}</span>
        {status === 'unavailable'
          ? ': mic disconnected'
          : status === 'silent'
            ? ': no sound from phone'
            : ': weak connection'}
      </span>
    </div>
  );
}
